import { useRouter } from "next/router";
import { createContext, useContext, useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";

const LoadingContext = createContext(null);

export const LoadingProvider = ({ children }) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      setIsInitialLoad(false);
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const startLoading = (message = "Loading Page") => {
    setLoadingMessage(message);
    setIsLoading(true);
  };

  const stopLoading = () => {
    setIsLoading(false);
    setLoadingMessage("Loading Page");
  };

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingMessage,
        startLoading,
        stopLoading,
        isInitialLoad,
      }}
    >
      {children}
      <AnimatePresence mode="wait">
        {isLoading && (
          <div
            className="fixed inset-0 bg-[#0d0d10] z-[999999] flex items-center justify-center"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ 
                duration: 0.3,
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="flex flex-col items-center gap-6"
            >
              <motion.div
                className="relative w-32 h-[50px]"
                animate={{
                  filter: [
                    "drop-shadow(0 0 0px rgba(250, 44, 180, 0))",
                    "drop-shadow(0 0 10px rgba(250, 44, 180, 0.3))",
                    "drop-shadow(0 0 0px rgba(250, 44, 180, 0))",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Image
                  src="/assets/logo.webp"
                  alt="Qoncert Logo"
                  fill
                  sizes="128px"
                  className="object-contain"
                  priority
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.2 }}
                className="flex gap-3"
              >
                {[0, 1, 2].map((index) => (
                  <motion.div
                    key={index}
                    className="w-3 h-3 bg-[#FA2CB4] rounded-full"
                    animate={{
                      y: [0, -3, 0],
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: index * 0.2,
                      ease: [0.4, 0, 0.6, 1],
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};
